import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import Select from "antd/es/select";
import sortBy from "lodash-es/sortBy";
import { useIntl } from "react-intl";

import User from "@mapmycustomers/shared/types/User";
import { AvatarWithName } from "@mapmycustomers/ui";

import SelectField from "@app/component/input/SelectField";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { userDisplayName } from "@app/util/formatters";

import styles from "./SharingFields.module.scss";

const DEFAULT_OPTION_KEY = -1;
const Option = Select.Option;

interface Props {
  onChange?: (userIds: User["id"][]) => void;
  users: User[];
  value?: User["id"][];
}

const SharingFields: React.FC<Props> = ({ onChange, users, value }) => {
  const intl = useIntl();

  const sortedUsers = useMemo(() => sortBy(users, [userDisplayName]), [users]);
  const userFilterOption = (inputValue: string, option: any) => {
    const user = users.find((type) => type.id === parseInt(option?.value));
    return ((user?.fullName ?? "") + (user?.username ?? ""))
      .toLowerCase()
      .includes(inputValue.toLowerCase().trim());
  };

  const handleUserSelect = useCallback(
    (userIds: User["id"][]) => {
      if (userIds.includes(DEFAULT_OPTION_KEY)) {
        onChange?.(users.map(({ id }) => id));
      } else {
        onChange?.(userIds);
      }
    },
    [onChange, users]
  );

  return (
    <SelectField<User["id"][]>
      className={styles.selectField}
      dropdownMatchSelectWidth={false}
      filterOption={userFilterOption}
      label={intl.formatMessage({
        id: "createTerritoryForm.field.userSelectField",
        defaultMessage: "Assign records within territory bounds to",
        description: "Field label for territories User multi select field",
      })}
      mode="multiple"
      onChange={handleUserSelect}
      placeholder={intl.formatMessage({
        id: "createTerritoryForm.field.userSelectField.placeholder",
        defaultMessage: "Type to search...",
        description: "Placeholder in territories User multi select field",
      })}
      value={value}
    >
      <Option key={DEFAULT_OPTION_KEY} value={DEFAULT_OPTION_KEY}>
        {intl.formatMessage({
          id: "createTerritoryForm.field.userSelectField.allUsers",
          defaultMessage: "Select all users in list",
          description: "All Users option name for territories User multi select field",
        })}
      </Option>
      )
      {sortedUsers.map((user) => (
        <Option key={user.id} label={userDisplayName(user)} value={user.id}>
          <AvatarWithName avatarClassName={styles.avatar} user={user} />
        </Option>
      ))}
    </SelectField>
  );
};

const mapStateToProps = (state: RootState) => ({
  users: getUsers(state),
});

export default connect(mapStateToProps)(SharingFields);
